import {
  AfterViewInit, ApplicationRef,
  Component, ComponentFactoryResolver,
  ElementRef,
  EventEmitter, Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import interact from 'interactjs';
import { SynchronizationService } from '../../../../services/synchronization.service';
import { v4 as uuid } from 'uuid';
import { Subscription } from 'rxjs';
import { ClonedImageComponent } from './cloned-image/cloned-image.component';
import { ClonedImageDirective } from './cloned-image/cloned-image.directive';

@Component({
  selector: 'app-gallery-image',
  templateUrl: './gallery-image.component.html',
  styleUrls: ['./gallery-image.component.scss']
  })
export class GalleryImageComponent implements OnInit, AfterViewInit, OnDestroy {
  data: any;
  @ViewChild('divcontainer', { static: true }) private element: ElementRef;
  @ViewChild(ClonedImageDirective) clonedImageDirective: ClonedImageDirective;
  @Input() image;
  @Input() selectedProject;
  @Output() deleteCallback: EventEmitter<any> = new EventEmitter();
  private event: Subscription[] = [];
  defaultImage = false;

  constructor(
    private synchronizationService: SynchronizationService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private app: ApplicationRef
  ) { }

  ngOnInit() {
  }

  ngAfterViewInit() {
    this.setupRealtimeEvents();
    this.setupDraggableImages();
  }

  ngOnDestroy(): void {
    for (const ev of this.event) {
      ev.unsubscribe();
    }
    interact(`.readytodrag${this.image.id}`).unset();
  }

  /**
   * Set up the event listeners for real-time synchronization.
   */
  private setupRealtimeEvents() {
    this.event.push(this.synchronizationService.subscribeEvent<any>(`GalleryImage${this.image.id}Hold`)
      .subscribe(data => {
        const clonedImage = this.copyImage(data.uuid);
        clonedImage.element.nativeElement.style.left = data.left;
        clonedImage.element.nativeElement.style.top = data.top;
      }));
  }

  /**
   * Set up the drag interactions on the image.
   */
  private setupDraggableImages() {
    // hold on the image
    interact(`.readytodrag${this.image.id}`)
      .on('hold', (event) => {
        const interaction = event.interaction;

        // if the pointer was moved while being held down
        // and an interaction hasn't started yet
        if (interaction.pointerIsDown && !interaction.interacting()) {
          const uniqueId = uuid();
          const clonedImage: ClonedImageComponent = this.copyImage(uniqueId);

          // set new position approximately at the center of the current cursor
          clonedImage.element.nativeElement.style.left = (event.clientX - screen.height * 0.06 + 5) + 'px'; // 0.06 is equals to 12vh / 2
          clonedImage.element.nativeElement.style.top = (event.clientY - screen.height * 0.06 + 5) + 'px'; // 0.06 is equals to 12vh / 2

          this.synchronizationService.sendRealTimeEvent(`GalleryImage${this.image.id}Hold`, {
            uuid: uniqueId,
            left: clonedImage.element.nativeElement.style.left,
            top: clonedImage.element.nativeElement.style.top
          });

          this.event.push(clonedImage.initEnd.subscribe(() => {
            interaction.start(
              { name: 'drag' },
              interact(`.readytodrag${uniqueId}`),
              clonedImage.element.nativeElement);
          }));
        }
      });

    // to prevent loosing "hold"-interaction on an image when moving the cursor a bit
    interact.pointerMoveTolerance(15);
  }



  /**
   * Copies the actual image and appends it to the informationview.
   * @param: uniqueId The unique id the copy of the image should have. (for example use uuid())
   * @returns The instance of the created component of the copied image.
   */
  copyImage(uniqueId: string): ClonedImageComponent {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(ClonedImageComponent);
    const targetParent: HTMLElement = <HTMLElement>document.getElementsByClassName('informationview')[0];
    const tmpNode = document.createElement('div');
    targetParent.appendChild(tmpNode);
    const componentRef = componentFactory.create(this.injector, [], tmpNode);
    this.app.attachView(componentRef.hostView);
    targetParent.appendChild(componentRef.instance.element.nativeElement);
    tmpNode.remove();

    (<ClonedImageComponent>componentRef.instance).image = this.image;
    (<ClonedImageComponent>componentRef.instance).uniqueId = uniqueId;
    return <ClonedImageComponent>componentRef.instance;
  }

  /**
   * Deletes an image
   * @param event Fired event from the target element.
   */
  deleteImage(event: any) {
    this.deleteCallback.emit([this.image]);
  }

}
